import React, { useContext, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import honourFragmentIdLinks from "../../../utils/honourFragmentIdLinks";
import {
  ACR_EDITOR_ABOUT_URL,
  ACR_EDITOR_CHAPTER_URL,
  ACR_EDITOR_REPORT_URL,
} from "../../../utlities/core.constants";
import { EvaluationContext } from "../stores/EvaluationContext";
import { CatalogContext } from "../stores/CatalogContext";
import {
  Header,
  HelpText,
  Criteria,
  LinkToGuidance,
  Pager,
  ChapterHelpText,
  ExpandCollapseAll,
} from "../components";
import EditorPagerLink from "../components/EditorPagerLink";
import {
  compareVersionStrings,
  isWcagStandard,
  reportCriterionRowId,
  wcagVersionFromText,
} from "../utils/acrExport/openAcrReportShared";

/** Chapter page for the ACR editor (ported from GSA openacr-editor). */

export default function Chapter({ chapterId: chapterIdProp = null, className }) {
  const { chapterId: chapterIdParam } = useParams();
  const chapterId = chapterIdProp ?? chapterIdParam ?? null;
  const location = useLocation();
  const { evaluation, setEvaluation } = useContext(EvaluationContext);
  const { getCatalog } = useContext(CatalogContext);

  const catalog = useMemo(
    () => getCatalog(evaluation?.catalog),
    [getCatalog, evaluation?.catalog]
  );
  const { standards = [], chapters = [] } = catalog;

  const currentChapter = useMemo(
    () => chapters.find(({ id }) => id === chapterId),
    [chapters, chapterId]
  );
  const currentChapterKey = useMemo(
    () => chapters.findIndex(({ id }) => id === chapterId),
    [chapters, chapterId]
  );
  const currentStandard = useMemo(
    () => {
      const matches = standards.filter(({ chapters: chIds }) => chIds?.includes(chapterId));
      if (matches.length <= 1) return matches[0];

      // WCAG chapters are shared across WCAG standards (2.0/2.1/2.2). Prefer the
      // highest version available in the selected catalog for chapter-level guidance.
      const wcagMatches = matches.filter((standard) => isWcagStandard(standard));
      if (wcagMatches.length > 0) {
        return wcagMatches.reduce((best, candidate) => {
          const bestVersion = wcagVersionFromText(best?.label) || wcagVersionFromText(best?.id);
          const candidateVersion = wcagVersionFromText(candidate?.label) || wcagVersionFromText(candidate?.id);
          return compareVersionStrings(candidateVersion, bestVersion) > 0 ? candidate : best;
        }, wcagMatches[0]);
      }

      return matches[0];
    },
    [standards, chapterId]
  );
  const standardHref = (currentStandard?.url ?? "").trim();
  const chapterHref = (currentChapter?.url ?? "").trim();
  const chapterDescription = (currentChapter?.description ?? "").trim();

  /** Ensure evaluation has criteria for this chapter so Component can render select/textarea. */
  useEffect(() => {
    if (!chapterId || !currentChapter?.criteria?.length || !setEvaluation) return;
    const catalogCriteria = currentChapter.criteria;

    setEvaluation((prev) => {
      const prevChapters = prev?.chapters ?? {};
      const prevChapter = prevChapters[chapterId] ?? {};
      const existingCriteria = prevChapter.criteria ?? [];
      const existingByNum = new Map(existingCriteria.map((c) => [String(c.num), c]));

      const criteria = catalogCriteria.map((catalogCrit) => {
        const num = catalogCrit.id;
        const existing = existingByNum.get(String(num));
        const componentNames = (catalogCrit.components || []).map((co) =>
          typeof co === "string" ? co : co?.name
        );
        const existingComps = existing?.components ?? [];
        const existingByName = new Map(existingComps.map((co) => [co.name, co]));

        const components = componentNames.map((name) => {
          const existingComp = existingByName.get(name);
          return {
            name,
            adherence: existingComp?.adherence ?? { level: "", notes: "" },
          };
        });

        return { num, components };
      });

      const needsInit =
        existingCriteria.length !== catalogCriteria.length ||
        catalogCriteria.some((cc) => {
          const ex = existingByNum.get(String(cc.id));
          return !ex || (ex.components?.length ?? 0) !== (cc.components || []).length;
        });
      if (!needsInit) return prev;

      return {
        ...prev,
        chapters: {
          ...prevChapters,
          [chapterId]: { ...prevChapter, criteria },
        },
      };
    });
  }, [chapterId, currentChapter?.criteria, setEvaluation]);

  useEffect(() => {
    if (currentChapter?.label) {
      document.title = `${currentChapter.label} | ACR Editor | GSA`;
    }
    honourFragmentIdLinks(location);
  }, [currentChapter?.label, location]);

  const updateChapterField = (field, value) => {
    if (!chapterId || !setEvaluation) return;
    const prevChapters = evaluation?.chapters ?? {};
    const prevChapter = prevChapters[chapterId] ?? {};
    setEvaluation({
      ...evaluation,
      chapters: {
        ...prevChapters,
        [chapterId]: { ...prevChapter, [field]: value },
      },
    });
  };

  const chapterData = evaluation?.chapters?.[chapterId] ?? {};
  const notes = chapterData.notes ?? "";
  const disabled = Boolean(chapterData.disabled);
  const isWebChapter = chapterId === "web";

  if (!currentChapter) {
    return (
      <div className={className}>
        <p>Chapter not found.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <Header>{currentChapter.label}</Header>

      {(chapterHref || standardHref) && (
        <p>
          <LinkToGuidance href={chapterHref || standardHref}>
            {currentStandard.label}
          </LinkToGuidance>
        </p>
      )}
      {chapterDescription && <p className="mt-2 mb-4 text-gray-700">{chapterDescription}</p>}

      <ChapterHelpText chapterId={currentChapter.id} chapterLabel={currentChapter.label} />

      <div className="field mb-4">
        <label htmlFor="evaluation-chapter-notes" className="usa-label">
          Notes
          <textarea
            value={notes}
            onChange={(e) => updateChapterField("notes", e.target.value)}
            readOnly={isWebChapter}
            id="evaluation-chapter-notes"
            rows={3}
            className="usa-textarea"
          />
        </label>
        <HelpText type="chapters" field="notes" />
      </div>

      <div className="field">
        <label htmlFor={`evaluation-disabled-chapter-${chapterId}`}>
          <input
            type="checkbox"
            value={chapterId}
            checked={disabled}
            onChange={(e) => updateChapterField("disabled", e.target.checked)}
            disabled={isWebChapter}
            id={`evaluation-disabled-chapter-${chapterId}`}
          />
          {" Disabled?"}
        </label>
        <HelpText type="disabled_chapters" field={chapterId} />
      </div>

      <ExpandCollapseAll />

      {currentChapter.criteria?.map((criteria) => {
        const standardKey = currentStandard?.id ?? currentStandard?.label ?? "";
        const viewInReportTo = currentStandard
          ? `${ACR_EDITOR_REPORT_URL}#${reportCriterionRowId(standardKey, chapterId, criteria.id)}`
          : null;
        return (
          <Criteria
            key={criteria.id}
            chapterId={chapterId}
            viewInReportTo={viewInReportTo}
            id={criteria.id}
            handle={criteria.handle}
            url={criteria.url}
            components={criteria.components}
          />
        );
      })}

      <Pager label="Previous/Next Chapter">
        {currentChapterKey === 0 && (
          <EditorPagerLink to={ACR_EDITOR_ABOUT_URL} direction="previous">
            About
          </EditorPagerLink>
        )}
        {currentChapterKey > 0 && (
          <EditorPagerLink
            to={`${ACR_EDITOR_CHAPTER_URL}/${chapters[currentChapterKey - 1].id}`}
            direction="previous"
          >
            {chapters[currentChapterKey - 1].short_label}
          </EditorPagerLink>
        )}
        {currentChapterKey + 1 < chapters.length && (
          <EditorPagerLink
            to={`${ACR_EDITOR_CHAPTER_URL}/${chapters[currentChapterKey + 1].id}`}
            direction="next"
          >
            {chapters[currentChapterKey + 1].short_label}
          </EditorPagerLink>
        )}
        {currentChapterKey + 1 === chapters.length && (
          <EditorPagerLink to={ACR_EDITOR_REPORT_URL} direction="next">
            View report
          </EditorPagerLink>
        )}
      </Pager>
    </div>
  );
}

Chapter.propTypes = {
  chapterId: PropTypes.string,
  className: PropTypes.string,
};

Chapter.defaultProps = {
  chapterId: null,
  className: undefined,
};
